import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  useToast,
  Progress,
  Icon,
  Card,
  CardBody,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiUpload, FiFile, FiCheck } from "react-icons/fi";
import { digitalFilesService } from "../digitalFilesService";

const DocumentModal = ({ isOpen, onClose, employee, onSuccess }) => {
  // Dark mode colors
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const bgColor = useColorModeValue("white", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const [formData, setFormData] = useState({
    documentType: "",
    documentName: "",
    documentDescription: "",
    issueDate: "",
    expiryDate: "",
    issuingAuthority: "",
    documentNumber: "",
    referenceNumber: "",
    tags: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const toast = useToast();

  const documentTypes = [
    "Resume/CV",
    "Offer Letter",
    "Employment Contract",
    "ID Proof",
    "Address Proof",
    "Educational Certificates",
    "Experience Letters",
    "Salary Slips",
    "Bank Statements",
    "Tax Documents",
    "Medical Certificates",
    "Training Certificates",
    "Performance Reviews",
    "Appraisal Letters",
    "Promotion Letters",
    "Termination/Resignation Letters",
    "NDA/Confidentiality Agreement",
    "Background Check Report",
    "Reference Letters",
    "Other",
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a file smaller than 10MB",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "image/gif",
        "text/plain",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description:
            "Please select PDF, DOC, DOCX, JPG, PNG, GIF, or TXT files",
          status: "error",
          duration: 3000,
        });
        return;
      }

      setSelectedFile(file);

      // Auto-fill document name if empty
      if (!formData.documentName) {
        setFormData((prev) => ({
          ...prev,
          documentName: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
        }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!employee || !selectedFile) {
      toast({
        title: "Missing information",
        description: "Please select an employee and a file to upload",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!formData.documentType || !formData.documentName) {
      toast({
        title: "Missing information",
        description: "Please fill in document type and name",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Create FormData for file upload
      const uploadData = new FormData();
      uploadData.append("document", selectedFile);
      uploadData.append("documentType", formData.documentType);
      uploadData.append("documentName", formData.documentName);
      uploadData.append(
        "documentDescription",
        formData.documentDescription || ""
      );
      uploadData.append("issueDate", formData.issueDate || "");
      uploadData.append("expiryDate", formData.expiryDate || "");
      uploadData.append("issuingAuthority", formData.issuingAuthority || "");
      uploadData.append("documentNumber", formData.documentNumber || "");
      uploadData.append("referenceNumber", formData.referenceNumber || "");
      uploadData.append("tags", formData.tags || "");

      // Simulate progress (in real implementation, use axios progress)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      await digitalFilesService.documents.uploadDocument(
        employee.employeeId,
        uploadData
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      toast({
        title: "Document uploaded successfully",
        status: "success",
        duration: 3000,
      });

      // Reset form
      setFormData({
        documentType: "",
        documentName: "",
        documentDescription: "",
        issueDate: "",
        expiryDate: "",
        issuingAuthority: "",
        documentNumber: "",
        referenceNumber: "",
        tags: "",
      });
      setSelectedFile(null);
      setUploadProgress(0);

      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "xl" }}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor}>
        <ModalHeader px={{ base: 4, md: 6 }}>
          Upload Document
          {employee && (
            <Text fontSize="sm" color={mutedColor}>
              for {employee.name}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 4, md: 6 }}>
          <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            {/* File Upload Section */}
            <Card>
              <CardBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Document File</FormLabel>
                    <Box
                      border="2px dashed"
                      borderColor="gray.300"
                      borderRadius="md"
                      p={6}
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ borderColor: "blue.400" }}
                      onClick={() =>
                        document.getElementById("file-input").click()
                      }
                    >
                      <input
                        id="file-input"
                        type="file"
                        hidden
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                      />
                      <Icon as={FiUpload} boxSize={8} color="gray.400" mb={2} />
                      <Text fontSize="lg" color={mutedColor}>
                        {selectedFile
                          ? selectedFile.name
                          : "Click to select a file"}
                      </Text>
                      <Text fontSize="sm" color={mutedColor}>
                        PDF, DOC, DOCX, JPG, PNG, GIF, TXT (max 10MB)
                      </Text>
                    </Box>
                  </FormControl>

                  {selectedFile && (
                    <Card bg="gray.50" w="full">
                      <CardBody>
                        <HStack spacing={3}>
                          <Icon as={FiFile} color="blue.500" />
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="medium">{selectedFile.name}</Text>
                            <Text fontSize="sm" color={mutedColor}>
                              {formatFileSize(selectedFile.size)} •{" "}
                              {selectedFile.type}
                            </Text>
                          </VStack>
                          <Icon as={FiCheck} color="green.500" />
                        </HStack>
                      </CardBody>
                    </Card>
                  )}

                  {uploading && (
                    <Box w="full">
                      <Text fontSize="sm" mb={2}>
                        Uploading...
                      </Text>
                      <Progress
                        value={uploadProgress}
                        colorScheme="blue"
                        size="sm"
                      />
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Document Information */}
            <Card>
              <CardBody>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Document Information
                </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl isRequired flex={1}>
                      <FormLabel>Document Type</FormLabel>
                      <Select
                        value={formData.documentType}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            documentType: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select document type</option>
                        {documentTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl isRequired flex={1}>
                      <FormLabel>Document Name</FormLabel>
                      <Input
                        value={formData.documentName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            documentName: e.target.value,
                          }))
                        }
                        placeholder="Enter document name"
                      />
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={formData.documentDescription}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          documentDescription: e.target.value,
                        }))
                      }
                      placeholder="Optional description"
                      rows={2}
                    />
                  </FormControl>

                  <HStack spacing={4} w="full">
                    <FormControl flex={1}>
                      <FormLabel>Issue Date</FormLabel>
                      <Input
                        type="date"
                        value={formData.issueDate}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            issueDate: e.target.value,
                          }))
                        }
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel>Expiry Date</FormLabel>
                      <Input
                        type="date"
                        value={formData.expiryDate}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            expiryDate: e.target.value,
                          }))
                        }
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="full">
                    <FormControl flex={1}>
                      <FormLabel>Issuing Authority</FormLabel>
                      <Input
                        value={formData.issuingAuthority}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            issuingAuthority: e.target.value,
                          }))
                        }
                        placeholder="e.g., University, Government"
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel>Document Number</FormLabel>
                      <Input
                        value={formData.documentNumber}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            documentNumber: e.target.value,
                          }))
                        }
                        placeholder="Certificate/Ref number"
                      />
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel>Reference Number</FormLabel>
                    <Input
                      value={formData.referenceNumber}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          referenceNumber: e.target.value,
                        }))
                      }
                      placeholder="Internal reference number"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Tags</FormLabel>
                    <Input
                      value={formData.tags}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          tags: e.target.value,
                        }))
                      }
                      placeholder="Comma-separated tags (e.g., important, confidential)"
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={uploading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={uploading}
            loadingText="Uploading..."
            isDisabled={
              !selectedFile || !formData.documentType || !formData.documentName
            }
          >
            Upload Document
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DocumentModal;
